import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Notifications as NotificationsIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  Dashboard as DashboardIcon,
  BookOnline as BookOnlineIcon,
  Receipt as ReceiptIcon,
  Person as PersonIcon,
  Feedback as FeedbackIcon,
  Home as HomeIcon
  , Menu as MenuIcon
} from '@mui/icons-material';

const drawerWidth = 240;

const UserDashboard = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(prev => !prev);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to logout:', error);
    }
    handleClose();
  };

  const isActive = (path) => location.pathname === path;

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/user/bookings' },
    { text: 'Book Service', icon: <BookOnlineIcon />, path: '/user/services' },
    { text: 'Invoices', icon: <ReceiptIcon />, path: '/user/invoices' }
  ];

  const profileItems = [
    { text: 'User Profile', icon: <PersonIcon />, path: '/user/profile' },
    { text: 'Feedback', icon: <FeedbackIcon />, path: '/user/feedback' }
  ];

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      {/* Left Sidebar */}
      <Drawer
        variant={isMdUp ? 'permanent' : 'temporary'}
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            borderRight: '1px solid #e0e0e0',
            backgroundColor: 'white'
          }
        }}
      >
        {/* Logo/Brand */}
        <Box sx={{ p: 2, m: 2, display: 'flex', alignItems: 'center', gap: 1.5, border: '2px solid #4A5BF4', borderRadius: 3, backgroundColor: 'white' }}>
          <Box
            sx={{
              backgroundColor: '#4A5BF4',
              color: 'white',
              width: 36,
              height: 36,
              borderRadius: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <HomeIcon sx={{ fontSize: 22 }} />
          </Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: '#4A5BF4',
              fontSize: '1.05rem',
              letterSpacing: '0.3px'
            }}
          >
            MyLocalForce
          </Typography>
        </Box>

        {/* User Info */}
        <Box sx={{ px: 2, pt: 1, pb: 2, mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, backgroundColor: '#F5F7FF', borderRadius: 2 }}>
            <Avatar
              sx={{
                width: 44,
                height: 44,
                backgroundColor: '#4A5BF4',
                fontWeight: 600,
                fontSize: '1.1rem'
              }}
            >
              {currentUser?.displayName?.charAt(0) || 'A'}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a1a1a', fontSize: '0.95rem' }}>
                {currentUser?.displayName || 'Ankush Mittal'}
              </Typography>
              <Typography variant="caption" sx={{ color: '#666', fontSize: '0.75rem' }}>
                {currentUser?.email || 'abcmittal@gmail.com'}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Main Menu */}
        <Box>
          <Typography variant="caption" sx={{ px: 3, color: '#9CA3AF', fontWeight: 600, fontSize: '0.7rem', letterSpacing: '0.5px' }}>
            MAIN MENU
          </Typography>
          <List sx={{ px: 2, mt: 1.5 }}>
            {menuItems.map((item) => (
              <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => navigate(item.path)}
                  selected={isActive(item.path)}
                  sx={{
                    borderRadius: 2,
                    py: 1.5,
                    px: 2,
                    '&.Mui-selected': {
                      backgroundColor: '#E8EDFF',
                      color: '#4A5BF4',
                      '& .MuiListItemIcon-root': {
                        color: '#4A5BF4'
                      },
                      '&:hover': {
                        backgroundColor: '#DDE4FF'
                      }
                    },
                    '&:hover': {
                      backgroundColor: '#F9FAFB'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: isActive(item.path) ? '#4A5BF4' : '#6B7280' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: '0.95rem',
                      fontWeight: isActive(item.path) ? 600 : 500,
                      color: isActive(item.path) ? '#4A5BF4' : '#374151'
                    }}
                  />
                  {isActive(item.path) && (
                    <Box sx={{ 
                      width: 4, 
                      height: 4, 
                      borderRadius: '50%', 
                      backgroundColor: '#4A5BF4',
                      ml: 1
                    }} />
                  )}
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>

        {/* Profile Section */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="caption" sx={{ px: 3, color: '#9CA3AF', fontWeight: 600, fontSize: '0.7rem', letterSpacing: '0.5px' }}>
            PROFILE
          </Typography>
          <List sx={{ px: 2, mt: 1.5 }}>
            {profileItems.map((item) => (
              <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => navigate(item.path)}
                  sx={{
                    borderRadius: 2,
                    py: 1.5,
                    px: 2,
                    '&:hover': {
                      backgroundColor: '#F9FAFB'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: '#6B7280' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: '0.95rem',
                      fontWeight: 500,
                      color: '#374151'
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>

        {/* Support Section */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="caption" sx={{ px: 3, color: '#9CA3AF', fontWeight: 600, fontSize: '0.7rem', letterSpacing: '0.5px' }}>
            SUPPORT
          </Typography>
          <List sx={{ px: 2, mt: 1.5 }}>
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                sx={{
                  borderRadius: 2,
                  py: 1.5,
                  px: 2,
                  '&:hover': {
                    backgroundColor: '#F9FAFB'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: '#6B7280' }}>
                  <FeedbackIcon />
                </ListItemIcon>
                <ListItemText 
                  primary="Feedback"
                  primaryTypographyProps={{
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    color: '#374151'
                  }}
                />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Drawer>

      {/* Main Content Area */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', ml: { md: `${drawerWidth}px` } }}>
        {/* Top App Bar */}
        <AppBar 
          position="sticky" 
          elevation={0}
          sx={{ 
            backgroundColor: 'white',
            borderBottom: '1px solid #e0e0e0'
          }}
        >
          <Toolbar sx={{ justifyContent: 'space-between', py: 1 }}>
            {!isMdUp && (
              <IconButton edge="start" onClick={handleDrawerToggle} sx={{ mr: 1 }}>
                <MenuIcon sx={{ color: '#666' }} />
              </IconButton>
            )}
            {/* Page Title */}
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 0.5 }}>
                {location.pathname === '/user/bookings' && 'My Bookings'}
                {location.pathname === '/user/services' && 'Book Service'}
                {location.pathname === '/user/invoices' && 'My Invoices'}
                {location.pathname === '/user/profile' && 'User Profile'}
                {location.pathname === '/user/feedback' && 'Feedback'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>
                {location.pathname === '/user/bookings' && 'view and update your bookings here'}
                {location.pathname === '/user/services' && 'browse and book services'}
                {location.pathname === '/user/invoices' && 'view your payment history'}
                {location.pathname === '/user/profile' && 'manage your account settings'}
                {location.pathname === '/user/feedback' && 'share your experience with us'}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton>
                <Badge badgeContent={3} color="error">
                  <NotificationsIcon sx={{ color: '#666' }} />
                </Badge>
              </IconButton>
              
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                AM
              </Typography>

              <IconButton onClick={handleMenu} sx={{ p: 0 }}>
                <Avatar
                  sx={{ 
                    width: 36, 
                    height: 36,
                    backgroundColor: '#4A5BF4',
                    fontWeight: 600,
                    fontSize: '0.9rem'
                  }}
                >
                  {currentUser?.displayName?.charAt(0) || 'A'}
                </Avatar>
              </IconButton>

              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                PaperProps={{
                  sx: {
                    mt: 1.5,
                    minWidth: 200,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                  }
                }}
              >
                <MenuItem onClick={handleClose}>
                  <AccountCircleIcon sx={{ mr: 1, fontSize: 20 }} />
                  User Profile
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                  <LogoutIcon sx={{ mr: 1, fontSize: 20 }} />
                  Logout
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Main Content */}
        <Box sx={{ flexGrow: 1 }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default UserDashboard;
